// src/utils/gardenShapeUtils.ts
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import earcut from 'earcut'; // Ensure earcut is installed (npm install earcut / yarn add earcut)
import { worldToLocal } from './threeUtils';

interface PolygonOptions {
    fillColor?: number;
    fillOpacity?: number;
    outlineColor?: number;
    outlineWidth?: number;
    vertexColor?: number;
    vertexSize?: number;
    renderOrderOffset?: number; // General offset
    isSelected?: boolean;
    showVertices?: boolean;
    useLocalCoords?: boolean;
    isElevationMode?: boolean; // For depth testing logic
    outlineOnly?: boolean; // <<< New: Only draw the outline and vertices
    customGroupName?: string; // Optional name for the group
}

/**
 * Creates a visual representation of a polygon with fill, outline, and vertices
 */
export function createPolygonVisualization(
    points: THREE.Vector3[], // Expects world coordinates
    container: HTMLElement,
    localOrigin: THREE.Vector3 | null, // World coordinate origin for this shape
    options: PolygonOptions = {}
) {
    if (!container || points.length < (options.outlineOnly ? 2 : 3)) { // Need 2 for line, 3 for fill
        console.warn("Cannot create polygon visualization: Missing requirements.", {
            hasContainer: !!container,
            points: points.length,
            minPoints: options.outlineOnly ? 2 : 3
        });
        return {
            group: null,
            elements: []
        };
    }

    const {
        fillColor = 0x00aa00,
        fillOpacity = 0.5,
        outlineColor = 0x00ff00,
        outlineWidth = 2.5,
        vertexColor = 0xffff00,
        vertexSize = 0.25,
        renderOrderOffset = 0,
        isSelected = false,
        showVertices = true,
        useLocalCoords = true, // Generally recommended for stability
        isElevationMode = false,
        outlineOnly = false, // <<< Default to false
        customGroupName = 'PolygonVisualizationGroup' // <<< Default group name
    } = options;

    const polygonGroup = new THREE.Group();
    polygonGroup.name = customGroupName; // Use the provided or default name
    const createdObjects: THREE.Object3D[] = [];

    // Use provided localOrigin or calculate if missing (though it should be provided)
    const origin = localOrigin || points[0]?.clone() || new THREE.Vector3();
    if (!localOrigin && points.length > 0) {
        console.warn("createPolygonVisualization called without localOrigin, using first point.");
    }
    polygonGroup.position.copy(origin); // Position group at the world origin

    // Convert world points to local coordinates relative to the group's origin
    const localPoints = useLocalCoords
        ? points.map(p => worldToLocal(p, origin))
        : points.map(p => p.clone().sub(origin)); // Simple offset

    // --- Create the fill mesh (only if not outlineOnly and enough points) ---
    if (!outlineOnly && localPoints.length >= 3) {
        const geometry = new THREE.BufferGeometry();
        const vertices3D: number[] = [];
        localPoints.forEach(p => vertices3D.push(p.x, p.y, p.z));
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices3D, 3));

        const vertices2D: number[] = [];
        // Project onto XY plane relative to local origin for triangulation
        localPoints.forEach(p => vertices2D.push(p.x, p.z)); // Use X and Z for ground plane triangulation? Or X/Y? Assuming X/Z for typical ground plans

        try {
            // Use earcut (ensure points define a non-self-intersecting polygon in the chosen 2D plane)
            const indices = earcut(vertices2D, undefined, 2); // Use x, z pairs
            if (indices && indices.length > 0) {
                 geometry.setIndex(indices);
                 geometry.computeVertexNormals(); // Important for lighting if used, also defines front/back face
            } else {
                 console.warn("Earcut returned no indices for triangulation.", { localPoints });
                 // Fallback? Maybe create N-sided polygon? For now, just skip fill.
            }
        } catch (e) {
            console.error("Earcut triangulation failed:", e);
             // Skip fill on error
        }


        // Proceed only if triangulation was successful
        if (geometry.index && geometry.index.count > 0) {
            const actualFillColor = isSelected ? 0x4488ff : fillColor;
            const actualFillOpacity = isSelected ? 0.7 : fillOpacity;

            const polygonMaterial = new THREE.MeshBasicMaterial({
                color: actualFillColor,
                transparent: true,
                opacity: actualFillOpacity,
                side: THREE.DoubleSide, // Render both sides
                depthTest: isElevationMode, // Keep depth test in elevation mode for the fill
                depthWrite: isElevationMode, // Keep depth write in elevation mode for the fill
                polygonOffset: !isElevationMode, // Use offset only when NOT in elevation mode to fight z-fighting with ground
                polygonOffsetFactor: isElevationMode ? 0 : -1,
                polygonOffsetUnits: isElevationMode ? 0 : -1,
            });

            const polygonMesh = new THREE.Mesh(geometry, polygonMaterial);
            polygonMesh.name = 'polygon_fill';
            // Render fill slightly behind outline/vertices in normal mode, render order 0 in elevation mode
            polygonMesh.renderOrder = isElevationMode ? 0 : (1000 + renderOrderOffset);
            polygonMesh.frustumCulled = false;
            polygonGroup.add(polygonMesh);
            createdObjects.push(polygonMesh);
        }
    }

    // --- Polygon Outline (always create if >= 2 points) ---
    if (localPoints.length >= 2) {
        const outlineRenderPoints: number[] = [];
        localPoints.forEach(p => outlineRenderPoints.push(p.x, p.y, p.z));
        // Explicitly close the loop for the outline visual if it's meant to be a closed shape
        if (localPoints.length > 0 && !localPoints[0].equals(localPoints[localPoints.length - 1])) {
            outlineRenderPoints.push(localPoints[0].x, localPoints[0].y, localPoints[0].z);
        } else if (localPoints.length === 0 && points.length >= 2 && points[0].equals(points[points.length-1])) {
            // handle case where original points list was already closed
             localPoints.forEach(p => outlineRenderPoints.push(p.x, p.y, p.z));
        }


        const outlineGeometry = new LineGeometry();
        // Check if outlineRenderPoints has enough data (at least two points, 6 numbers)
         if (outlineRenderPoints.length >= 6) {
            outlineGeometry.setPositions(outlineRenderPoints);

            const actualOutlineColor = isSelected ? 0x3366ff : outlineColor;
            const actualOutlineWidth = isSelected ? outlineWidth * 1.25 : outlineWidth;
            // Make outline more opaque if it's the only visual element
            const actualOutlineOpacity = outlineOnly ? 1.0 : 0.85;

            const outlineMaterial = new LineMaterial({
                color: actualOutlineColor,
                linewidth: actualOutlineWidth,
                resolution: new THREE.Vector2(
                    container.clientWidth || 1920,  // *** BUG FIX: Fallback to prevent 0 dimension ***
                    container.clientHeight || 1080   // *** BUG FIX: Fallback to prevent 0 dimension ***
                ),
                dashed: false,
                depthTest: false, // Always draw on top
                depthWrite: false,
                transparent: true,
                opacity: actualOutlineOpacity,
                alphaToCoverage: true,
            });
            const outline = new Line2(outlineGeometry, outlineMaterial);
            outline.name = 'polygon_outline';
            // Render outline above fill in normal mode, very high render order in elevation mode
            outline.renderOrder = isElevationMode ? 5000 : (outlineOnly ? 1500 : 2000 + renderOrderOffset);
            outline.frustumCulled = false;
            polygonGroup.add(outline);
            createdObjects.push(outline);
         } else {
             console.warn("Not enough points to draw outline.", { numCoords: outlineRenderPoints.length });
         }
    }

    // --- Vertex Highlights (only if showVertices is true and >= 1 point) ---
    if (showVertices && localPoints.length >= 1) {
        const actualVertexColor = isSelected ? 0x66aaff : vertexColor;
        const actualVertexSize = isSelected ? vertexSize * 1.25 : vertexSize;

        const uniqueLocalPoints = localPoints.filter((p, i, arr) => {
            if (i === arr.length - 1 && arr.length > 1 && p.equals(arr[0])) return false;
            return true;
        });

        uniqueLocalPoints.forEach((point, index) => {
            const vertexGeometry = new THREE.SphereGeometry(actualVertexSize, 16, 16);
            const vertexMaterial = new THREE.MeshBasicMaterial({
                color: actualVertexColor,
                depthTest: false,
                depthWrite: false,
                transparent: true,
                opacity: 0.9
            });
            const vertexMesh = new THREE.Mesh(vertexGeometry, vertexMaterial);
            vertexMesh.name = `vertex_${index}`;
            vertexMesh.position.copy(point);
            vertexMesh.renderOrder = isElevationMode ? 5100 : (outlineOnly ? 1600 : 3000 + renderOrderOffset);
            vertexMesh.frustumCulled = false;
            polygonGroup.add(vertexMesh);
            createdObjects.push(vertexMesh);
        });
    }

    return {
        group: polygonGroup,
        elements: createdObjects
    };
}